// Helper function to clean HTML from description
const cleanDescription = (htmlDescription) => {
  if (!htmlDescription) return '';

  // Remove HTML tags and normalize whitespace
  return htmlDescription
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with regular space
    .replace(/\s+/g, ' ') // Normalize multiple whitespace to single space
    .trim();
};

// Helper function to format date from timestamp
const formatDate = (timestamp) => {
  if (!timestamp) return '';

  return new Date(timestamp).toISOString().split('T')[0]; // Returns YYYY-MM-DD format
};

/**
 * Transform DOD JSON data specifically for the ProductGroup portion
 * This focuses only on the common title and description as requested
 *
 * @param {object} dealData - The DOD JSON data
 * @param {Array} allowedDealIds - Optional array of deal IDs to process. If provided, only deals in this list will be processed.
 * @returns {object} ProductGroup with basic properties, or null if deal ID not in allowed list
 */
const createProductGroup = (dealData, allowedDealIds = null) => {
  // Check if we should process this deal
  if (allowedDealIds && !allowedDealIds.includes(dealData.id)) {
    return null;
  }

  // Create the basic ProductGroup structure
  const productGroup = {
    '@context': 'https://schema.org',
    '@type': 'ProductGroup',
    description: cleanDescription(dealData?.description),
    name: dealData?.headline,
    productGroupID: dealData?.id?.toString(),
    url: `https://www.wowcher.co.uk${dealData?.urlPath || ''}`,
  };

  // Add aggregate rating if review summary exists
  if (dealData.reviewSummary && dealData.reviewSummary.total > 0) {
    productGroup.aggregateRating = {
      '@type': 'AggregateRating',
      bestRating: 5,
      ratingValue: dealData.reviewSummary.average,
      reviewCount: dealData.reviewSummary.total,
      worstRating: 1,
    };
  }

  // Add individual reviews if they exist
  if (dealData.reviews && dealData.reviews.length > 0) {
    productGroup.review = dealData.reviews.map((review) => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: review.nameLocation || 'Anonymous',
      },
      datePublished: formatDate(review.date),
      reviewBody: review.review,
      reviewRating: {
        '@type': 'Rating',
        bestRating: 5,
        ratingValue: review.rating,
        worstRating: 1,
      },
    }));
  }

  // Add all images (deal-level and product-specific) to ProductGroup
  const allProductGroupImages = [];

  // Add deal-level images first
  if (dealData.images && dealData.images.length > 0) {
    allProductGroupImages.push(
      ...dealData.images.map((image) => `${image.imageUrl}.${image.extension}`),
    );
  }

  // Add product-specific images from all products
  if (dealData.products && dealData.products.length > 0) {
    dealData.products.forEach((product) => {
      if (product.images && product.images.length > 0) {
        const productImageUrls = product.images.map(
          (img) => `${img.imageUrl}.${img.extension}`,
        );
        productImageUrls.forEach((imageUrl) => {
          if (!allProductGroupImages.includes(imageUrl)) {
            allProductGroupImages.push(imageUrl);
          }
        });
      }
    });
  }

  if (allProductGroupImages.length > 0) {
    productGroup.image = allProductGroupImages;
  }

  // Add hasVariant array with Product entities and variesBy
  if (dealData.products && dealData.products.length > 0) {
    // If only one product, return a simple Product structure instead of ProductGroup
    if (dealData.products.length === 1) {
      const product = dealData.products[0];

      // Create shipping details for the single product
      const shippingDetails = [];
      if (product.productDeliveryOptions) {
        product.productDeliveryOptions
          .filter((option) => option.deliveryOptionType !== 41) // Exclude Click and Collect
          .forEach((option) => {
            const handlingDays =
              (option.redemptionDays || 0) - (option.dispatchDays || 0);
            const maxHandlingDays = Math.max(handlingDays, 0);

            shippingDetails.push({
              '@id': `#shipping-${
                option.deliveryOptionType
              }-${option.customerPostageCost.toString().replace('.', '_')}`,
              '@type': 'OfferShippingDetails',
              deliveryTime: {
                '@type': 'ShippingDeliveryTime',
                handlingTime: {
                  '@type': 'QuantitativeValue',
                  maxValue: maxHandlingDays,
                  minValue: 0,
                  unitCode: 'DAY',
                },
                transitTime: {
                  '@type': 'QuantitativeValue',
                  maxValue: option.dispatchDays || 1,
                  minValue: option.dispatchDays || 1,
                  unitCode: 'DAY',
                },
              },
              shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: 'GB',
              },
              shippingLabel: option.deliveryOptionName,
              shippingRate: {
                '@type': 'MonetaryAmount',
                currency: 'GBP',
                value: option.customerPostageCost,
              },
            });
          });
      }

      // Create return policy for the single product
      const returnPolicy = {
        '@id': `#return-policy-${dealData.id}`,
        '@type': 'MerchantReturnPolicy',
        additionalProperty: [
          {
            '@type': 'PropertyValue',
            name: 'Refund Options',
            value: '100% cash refund or 100% credit refund available',
          },
          {
            '@type': 'PropertyValue',
            name: 'Condition',
            value:
              'Applies only to purchases that have not been redeemed. No physical return required.',
          },
        ],
        applicableCountry: 'GB',
        merchantReturnDays: 14,
        refundType: ['https://schema.org/FullRefund'],
        returnFees: 'https://schema.org/FreeReturn',
        returnPolicyCategory:
          'https://schema.org/MerchantReturnFiniteReturnWindow',
      };

      const cleanProductDescription = product.friendlyName
        ? product.friendlyName
            .replace(/\s*or redeem towards another available deal.*$/i, '')
            .trim()
        : '';

      // Determine pricing structure
      const pricing = determinePricing(
        product,
        dealData.currency?.toUpperCase() || 'GBP',
      );

      // Build price specifications array
      const priceSpecifications = [];

      if (pricing.isOnSale && pricing.originalPrice) {
        // Sale pricing with original price
        priceSpecifications.push({
          '@type': 'UnitPriceSpecification',
          price: pricing.currentPrice,
          priceCurrency: dealData.currency?.toUpperCase() || 'GBP',
        });

        priceSpecifications.push({
          '@type': 'UnitPriceSpecification',
          price: pricing.originalPrice,
          priceCurrency: dealData.currency?.toUpperCase() || 'GBP',
          priceType: 'https://schema.org/StrikethroughPrice',
        });
      } else {
        // Standard pricing
        priceSpecifications.push({
          '@type': 'UnitPriceSpecification',
          price: pricing.currentPrice,
          priceCurrency: dealData.currency?.toUpperCase() || 'GBP',
        });
      }

      // Add VIP pricing
      if (pricing.isOnSale && pricing.originalVipPrice) {
        priceSpecifications.push({
          '@type': 'UnitPriceSpecification',
          price: pricing.currentVipPrice,
          priceCurrency: dealData.currency?.toUpperCase() || 'GBP',
          validForMemberTier: {
            '@id': 'https://www.wowcher.co.uk/deals/vip',
            '@type': 'MemberProgramTier',
          },
        });

        priceSpecifications.push({
          '@type': 'UnitPriceSpecification',
          price: pricing.originalVipPrice,
          priceCurrency: dealData.currency?.toUpperCase() || 'GBP',
          priceType: 'https://schema.org/StrikethroughPrice',
          validForMemberTier: {
            '@id': 'https://www.wowcher.co.uk/deals/vip',
            '@type': 'MemberProgramTier',
          },
        });
      } else {
        priceSpecifications.push({
          '@type': 'UnitPriceSpecification',
          price: pricing.currentVipPrice,
          priceCurrency: dealData.currency?.toUpperCase() || 'GBP',
          validForMemberTier: {
            '@id': 'https://www.wowcher.co.uk/deals/vip',
            '@type': 'MemberProgramTier',
          },
        });
      }

      const singleProduct = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        description: cleanDescription(dealData.description),
        name: cleanProductDescription || dealData?.headline,
        offers: {
          '@type': 'Offer',
          availability: 'https://schema.org/InStock',
          hasMerchantReturnPolicy: { '@id': returnPolicy['@id'] },
          price: pricing.currentPrice,
          priceCurrency: dealData.currency?.toUpperCase() || 'GBP',
          priceSpecification: priceSpecifications,
          priceValidUntil:
            pricing.priceValidUntil ||
            (dealData.closingDate
              ? new Date(dealData.closingDate).toISOString().split('T')[0]
              : undefined),
          shippingDetails: shippingDetails.map((shipping) => ({
            '@id': shipping['@id'],
          })),
        },
        productID: dealData?.id?.toString(),
        url: createProductUrl(
          dealData?.urlPath,
          dealData.id,
          product,
          dealData,
        ),
      };

      // Add aggregate rating if review summary exists
      if (dealData.reviewSummary && dealData.reviewSummary.total > 0) {
        singleProduct.aggregateRating = {
          '@type': 'AggregateRating',
          bestRating: 5,
          ratingValue: dealData.reviewSummary.average,
          reviewCount: dealData.reviewSummary.total,
          worstRating: 1,
        };
      }

      // Add individual reviews if they exist
      if (dealData.reviews && dealData.reviews.length > 0) {
        singleProduct.review = dealData.reviews.map((review) => ({
          '@type': 'Review',
          author: {
            '@type': 'Person',
            name: review.nameLocation || 'Anonymous',
          },
          datePublished: formatDate(review.date),
          reviewBody: review.review,
          reviewRating: {
            '@type': 'Rating',
            bestRating: 5,
            ratingValue: review.rating,
            worstRating: 1,
          },
        }));
      }

      // Add GTIN if available and not 0
      if (product.gtin && product.gtin !== 0 && product.gtin !== '0') {
        singleProduct.gtin = product.gtin;
      }

      // Add itemCondition (omit if refurbished)
      if (!isRefurbished(dealData)) {
        singleProduct.itemCondition = 'https://schema.org/NewCondition';
      }

      // Add deal-level images to the single product
      if (dealData.images && dealData.images.length > 0) {
        singleProduct.image = dealData.images.map(
          (image) => `${image.imageUrl}.${image.extension}`,
        );
      }

      // Include shipping details and return policy using @graph structure
      if (shippingDetails.length > 0 || returnPolicy) {
        return {
          '@context': 'https://schema.org',
          '@graph': [singleProduct, ...shippingDetails, returnPolicy],
        };
      }

      return singleProduct;
    }

    // Multiple products - use ProductGroup structure
    const variantInfo = createProductVariants(dealData);
    productGroup.hasVariant = variantInfo.variants;

    // Add variesBy array if variants exist
    if (variantInfo.variesBy && variantInfo.variesBy.length > 0) {
      productGroup.variesBy = variantInfo.variesBy;
    }

    // Add return policy reference to ProductGroup
    if (variantInfo.returnPolicy) {
      productGroup.hasMerchantReturnPolicy = {
        '@id': variantInfo.returnPolicy['@id'],
      };
    }

    // Include distinct shipping details and return policy in the output using @graph structure
    const additionalObjects = [];
    if (variantInfo.shippingDetails && variantInfo.shippingDetails.length > 0) {
      additionalObjects.push(...variantInfo.shippingDetails);
    }
    if (variantInfo.returnPolicy) {
      additionalObjects.push(variantInfo.returnPolicy);
    }

    if (additionalObjects.length > 0) {
      return {
        '@context': 'https://schema.org',
        '@graph': [productGroup, ...additionalObjects],
      };
    }
  }

  return productGroup;
};

/**
 * Creates Product variants based on the productDisplay and products data
 *
 * @param {object} dealData - The DOD JSON data
 * @returns {object} Object with variants array, variesBy array, distinct shipping details, and return policy
 */
const createProductVariants = (dealData) => {
  const variants = [];
  const variesBy = [];

  // Create the distinct return policy object
  const returnPolicy = {
    '@id': `#return-policy-${dealData.id}`,
    '@type': 'MerchantReturnPolicy',
    additionalProperty: [
      {
        '@type': 'PropertyValue',
        name: 'Refund Options',
        value: '100% cash refund or 100% credit refund available',
      },
      {
        '@type': 'PropertyValue',
        name: 'Condition',
        value:
          'Applies only to purchases that have not been redeemed. No physical return required.',
      },
    ],
    applicableCountry: 'GB',
    merchantReturnDays: 14,
    refundType: ['https://schema.org/FullRefund'],
    returnFees: 'https://schema.org/FreeReturn',
    returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
  };

  // Collect all unique shipping options across all products
  const shippingOptionsMap = new Map();

  dealData.products.forEach((product) => {
    if (product.productDeliveryOptions) {
      product.productDeliveryOptions
        .filter((option) => option.deliveryOptionType !== 41) // Exclude Click and Collect
        .forEach((option) => {
          // Create a unique key for this shipping option
          const key = `${option.deliveryOptionType}-${option.customerPostageCost}-${option.dispatchDays}-${option.redemptionDays}`;

          if (!shippingOptionsMap.has(key)) {
            const handlingDays =
              (option.redemptionDays || 0) - (option.dispatchDays || 0);
            const maxHandlingDays = Math.max(handlingDays, 0);

            shippingOptionsMap.set(key, {
              '@id': `#shipping-${
                option.deliveryOptionType
              }-${option.customerPostageCost.toString().replace('.', '_')}`,
              '@type': 'OfferShippingDetails',
              deliveryTime: {
                '@type': 'ShippingDeliveryTime',
                handlingTime: {
                  '@type': 'QuantitativeValue',
                  maxValue: maxHandlingDays,
                  minValue: 0,
                  unitCode: 'DAY',
                },
                transitTime: {
                  '@type': 'QuantitativeValue',
                  maxValue: option.dispatchDays || 1,
                  minValue: option.dispatchDays || 1,
                  unitCode: 'DAY',
                },
              },
              shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: 'GB',
              },
              shippingLabel: option.deliveryOptionName,
              shippingRate: {
                '@type': 'MonetaryAmount',
                currency: 'GBP',
                value: option.customerPostageCost,
              },
            });
          }
        });
    }
  });

  // Convert map to array of shipping details
  const distinctShippingDetails = Array.from(shippingOptionsMap.values());

  // First, collect all unique row and column IDs actually used by products
  const usedRowIds = new Set();
  const usedColumnIds = new Set();

  dealData.products.forEach((product) => {
    if (product.rowId) usedRowIds.add(product.rowId);
    if (product.columnId) usedColumnIds.add(product.columnId);
  });

  // Create lookup maps for row and column variant info (only for used IDs)
  const rowVariants = {};
  const columnVariants = {};
  let rowVariantType = null;
  let columnVariantType = null;

  if (dealData.productDisplay) {
    // Map row variants (usually size) - only for IDs used by products
    if (dealData.productDisplay.row && dealData.productDisplay.row.items) {
      rowVariantType = getVariantType(dealData.productDisplay.row.header);
      dealData.productDisplay.row.items.forEach((item) => {
        if (usedRowIds.has(item.id)) {
          rowVariants[item.id] = {
            type: rowVariantType,
            value: item.header,
          };
        }
      });
    }

    // Map column variants (usually color) - only for IDs used by products
    if (
      dealData.productDisplay.column &&
      dealData.productDisplay.column.items
    ) {
      columnVariantType = getVariantType(dealData.productDisplay.column.header);
      dealData.productDisplay.column.items.forEach((item) => {
        if (usedColumnIds.has(item.id)) {
          columnVariants[item.id] = {
            type: columnVariantType,
            value: item.header,
          };
        }
      });
    }
  }

  // Build variesBy array based on meaningful variants (filtered to size/color only)
  if (
    rowVariantType &&
    isMeaningfulVariant(dealData.productDisplay?.row) &&
    isValidVariesByType(rowVariantType)
  ) {
    variesBy.push(getSchemaOrgUrl(rowVariantType));
  }
  if (
    columnVariantType &&
    isMeaningfulVariant(dealData.productDisplay?.column) &&
    isValidVariesByType(columnVariantType)
  ) {
    variesBy.push(getSchemaOrgUrl(columnVariantType));
  }

  // Create Product variants
  dealData.products.forEach((product) => {
    // Clean the product description by removing the redemption text
    const cleanProductDescription = product.friendlyName
      ? product.friendlyName
          .replace(/\s*or redeem towards another available deal.*$/i, '')
          .trim()
      : '';

    // Get shipping detail references for this product
    const shippingDetailReferences = [];
    if (product.productDeliveryOptions) {
      product.productDeliveryOptions
        .filter((option) => option.deliveryOptionType !== 41)
        .forEach((option) => {
          const key = `${option.deliveryOptionType}-${option.customerPostageCost}-${option.dispatchDays}-${option.redemptionDays}`;
          if (shippingOptionsMap.has(key)) {
            const shippingDetail = shippingOptionsMap.get(key);
            shippingDetailReferences.push({ '@id': shippingDetail['@id'] });
          }
        });
    }

    // Determine pricing structure for this variant
    const pricing = determinePricing(
      product,
      dealData.currency?.toUpperCase() || 'GBP',
    );

    // Build price specifications array for variant
    const priceSpecifications = [];

    if (pricing.isOnSale && pricing.originalPrice) {
      // Sale pricing with original price
      priceSpecifications.push({
        '@type': 'UnitPriceSpecification',
        price: pricing.currentPrice,
        priceCurrency: dealData.currency?.toUpperCase() || 'GBP',
      });

      priceSpecifications.push({
        '@type': 'UnitPriceSpecification',
        price: pricing.originalPrice,
        priceCurrency: dealData.currency?.toUpperCase() || 'GBP',
        priceType: 'https://schema.org/StrikethroughPrice',
      });
    } else {
      // Standard pricing
      priceSpecifications.push({
        '@type': 'UnitPriceSpecification',
        price: pricing.currentPrice,
        priceCurrency: dealData.currency?.toUpperCase() || 'GBP',
      });
    }

    // Add VIP pricing for variant
    if (pricing.isOnSale && pricing.originalVipPrice) {
      priceSpecifications.push({
        '@type': 'UnitPriceSpecification',
        price: pricing.currentVipPrice,
        priceCurrency: dealData.currency?.toUpperCase() || 'GBP',
        validForMemberTier: {
          '@id': 'https://www.wowcher.co.uk/deals/vip',
          '@type': 'MemberProgramTier',
        },
      });

      priceSpecifications.push({
        '@type': 'UnitPriceSpecification',
        price: pricing.originalVipPrice,
        priceCurrency: dealData.currency?.toUpperCase() || 'GBP',
        priceType: 'https://schema.org/StrikethroughPrice',
        validForMemberTier: {
          '@id': 'https://www.wowcher.co.uk/deals/vip',
          '@type': 'MemberProgramTier',
        },
      });
    } else {
      priceSpecifications.push({
        '@type': 'UnitPriceSpecification',
        price: pricing.currentVipPrice,
        priceCurrency: dealData.currency?.toUpperCase() || 'GBP',
        validForMemberTier: {
          '@id': 'https://www.wowcher.co.uk/deals/vip',
          '@type': 'MemberProgramTier',
        },
      });
    }

    const variant = {
      '@type': 'Product',
      description: cleanProductDescription || dealData?.headline,
      name: cleanProductDescription,
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        hasMerchantReturnPolicy: { '@id': returnPolicy['@id'] },
        price: pricing.currentPrice,
        priceCurrency: dealData.currency?.toUpperCase() || 'GBP',
        priceSpecification: priceSpecifications,
        priceValidUntil:
          pricing.priceValidUntil ||
          (dealData.closingDate
            ? new Date(dealData.closingDate).toISOString().split('T')[0]
            : undefined),
        shippingDetails: shippingDetailReferences,
      },
      productID: product.id.toString(),
      url: createProductUrl(dealData.urlPath, product.id, product, dealData),
    };

    // Add GTIN if available and not 0
    if (product.gtin && product.gtin !== 0 && product.gtin !== '0') {
      variant.gtin = product.gtin;
    }

    // Add itemCondition (omit if refurbished)
    if (!isRefurbished(dealData)) {
      variant.itemCondition = 'https://schema.org/NewCondition';
    }

    // Add variant properties directly to the Product (consistent for all variants if included in variesBy)
    if (
      product.rowId &&
      rowVariants[product.rowId] &&
      isMeaningfulVariant(dealData.productDisplay.row)
    ) {
      const variantType = rowVariants[product.rowId].type;
      const variantValue = rowVariants[product.rowId].value;

      // Only add property if this variant type is included in variesBy
      if (isValidVariesByType(variantType)) {
        variant[variantType] = variantValue;
      }
    }

    if (
      product.columnId &&
      columnVariants[product.columnId] &&
      isMeaningfulVariant(dealData.productDisplay.column)
    ) {
      const variantType = columnVariants[product.columnId].type;
      const variantValue = columnVariants[product.columnId].value;

      // Only add property if this variant type is included in variesBy
      if (isValidVariesByType(variantType)) {
        variant[variantType] = variantValue;
      }
    }

    // Add images to the variant
    if (product.images && product.images.length > 0) {
      // Use product-specific images if they exist
      variant.image = product.images.map(
        (img) => `${img.imageUrl}.${img.extension}`,
      );
    } else if (dealData.images && dealData.images.length > 0) {
      // Fall back to the first image from the product group
      const firstImage = dealData.images[0];
      variant.image = [`${firstImage.imageUrl}.${firstImage.extension}`];
    }

    variants.push(variant);
  });

  return {
    returnPolicy,
    shippingDetails: distinctShippingDetails,
    variants,
    variesBy,
  };
};

/**
 * Determines the variant type from the header text
 *
 * @param {string} header - The header text like "your size:", "your colour:"
 * @returns {string} Normalized variant type
 */
const getVariantType = (header) => {
  if (!header) return 'variant';

  const lowerHeader = header.toLowerCase();

  if (lowerHeader.includes('size')) return 'size';
  if (lowerHeader.includes('colour') || lowerHeader.includes('color'))
    return 'color';
  if (lowerHeader.includes('style')) return 'style';

  // Extract the word between "your" and ":" if present
  const match = lowerHeader.match(/your\s+([^:]+):/);
  if (match) {
    return match[1].trim();
  }

  return 'variant';
};

/**
 * Maps variant types to Schema.org URLs
 *
 * @param {string} variantType - The variant type (size, color, etc.)
 * @returns {string} Schema.org URL for the variant type
 */
const getSchemaOrgUrl = (variantType) => {
  const schemaOrgMappings = {
    color: 'https://schema.org/color',
    colour: 'https://schema.org/color',
    height: 'https://schema.org/height',
    length: 'https://schema.org/depth',
    material: 'https://schema.org/material',
    pattern: 'https://schema.org/pattern',
    size: 'https://schema.org/size',
    style: 'https://schema.org/pattern',
    weight: 'https://schema.org/weight',
    width: 'https://schema.org/width',
  };

  return (
    schemaOrgMappings[variantType.toLowerCase()] ||
    `https://schema.org/${variantType}`
  );
};

/**
 * Checks if a variant dimension (row/column) contains meaningful variants
 *
 * @param {object} variantDimension - The row or column object from productDisplay
 * @returns {boolean} True if the variant is meaningful, false if it's just a placeholder
 */
const isMeaningfulVariant = (variantDimension) => {
  if (
    !variantDimension ||
    !variantDimension.header ||
    !variantDimension.items
  ) {
    return false;
  }

  // Check if header is meaningful (not just "-" or empty)
  const header = variantDimension.header.toLowerCase().trim();
  if (header === '-' || header === '' || header === 'variant') {
    return false;
  }

  // Check if there are multiple meaningful options
  if (variantDimension.items.length <= 1) {
    return false;
  }

  // Check if the items contain meaningful values (not just "-" or similar)
  const meaningfulItems = variantDimension.items.filter((item) => {
    const itemHeader = item.header.toLowerCase().trim();

    return itemHeader !== '-' && itemHeader !== '' && itemHeader !== 'n/a';
  });

  return meaningfulItems.length > 1;
};

/**
 * Checks if a variant type should be included in variesBy array
 *
 * @param {string} variantType - The variant type (size, color, option, etc.)
 * @returns {boolean} True if the variant type is size or color
 */
const isValidVariesByType = (variantType) => {
  if (!variantType || typeof variantType !== 'string') {
    return false;
  }

  const lowerType = variantType.toLowerCase();

  return (
    lowerType === 'size' || lowerType === 'color' || lowerType === 'colour'
  );
};

/**
 * Determines the pricing structure from product price schedules
 *
 * @param {object} product - The product object with productPriceSchedules
 * @param {string} currency - The currency code
 * @returns {object} Pricing information with sale and original prices
 */
const determinePricing = (product, currency = 'GBP') => {
  if (
    !product.productPriceSchedules ||
    product.productPriceSchedules.length <= 1
  ) {
    // Single or no price schedule - use standard pricing
    return {
      currentPrice: product.price,
      currentVipPrice: product.vipPrice,
      isOnSale: false,
      originalPrice: null,
      originalVipPrice: null,
      priceValidUntil: null,
    };
  }

  // Multiple price schedules - determine sale vs original pricing
  const schedules = product.productPriceSchedules
    .filter((schedule) => schedule.startDate) // Only schedules with start dates
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate)); // Sort by start date

  if (schedules.length < 2) {
    // Fallback to standard pricing
    return {
      currentPrice: product.price,
      currentVipPrice: product.vipPrice,
      isOnSale: false,
      originalPrice: null,
      originalVipPrice: null,
      priceValidUntil: null,
    };
  }

  // Earlier schedule = original price, later schedule = sale price
  const originalSchedule = schedules[0];
  const saleSchedule = schedules[schedules.length - 1];

  return {
    currentPrice: saleSchedule.schedulePrice,
    currentVipPrice: saleSchedule.vipPrice,
    isOnSale: true,
    originalPrice: originalSchedule.schedulePrice,
    originalVipPrice: originalSchedule.vipPrice,
    priceValidUntil: saleSchedule.endDate
      ? new Date(saleSchedule.endDate).toISOString().split('T')[0]
      : null,
  };
};

// Helper function to convert text to URL-friendly format (lowercase and hyphenated)
const toUrlFormat = (text) => {
  if (!text) return '';

  return text
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/[^\da-z-]/g, '') // Remove special characters except hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Creates a product-specific URL by inserting the product ID and variant names before the last segment
 *
 * @param {string} baseUrlPath - The base URL path from dealData.urlPath
 * @param {string|number} productId - The product ID or deal ID to insert
 * @param {object} product - The product object with rowId and columnId
 * @param {object} dealData - The DOD data containing productDisplay info
 * @returns {string} Full product URL
 */
const createProductUrl = (baseUrlPath, productId, product, dealData) => {
  if (!baseUrlPath || !productId) {
    return '';
  }

  // Get row and column variant names
  let rowName = '';
  let columnName = '';

  if (dealData && dealData.productDisplay && product) {
    // Get row name (usually size)
    if (
      product.rowId &&
      dealData.productDisplay.row &&
      dealData.productDisplay.row.items
    ) {
      const rowItem = dealData.productDisplay.row.items.find(
        (item) => item.id === product.rowId,
      );
      if (rowItem && rowItem.header) {
        rowName = toUrlFormat(rowItem.header);
      }
    }

    // Get column name (usually color)
    if (
      product.columnId &&
      dealData.productDisplay.column &&
      dealData.productDisplay.column.items
    ) {
      const columnItem = dealData.productDisplay.column.items.find(
        (item) => item.id === product.columnId,
      );
      if (columnItem && columnItem.header) {
        columnName = toUrlFormat(columnItem.header);
      }
    }
  }

  // Split the URL path by '/'
  const segments = baseUrlPath.split('/');

  // Build the new last segment with variants
  if (segments.length > 0) {
    const originalLastSegment = segments.pop(); // Remove and get the last segment
    segments.push(productId.toString()); // Add product ID

    // Build the final segment with variant names
    let finalSegment = originalLastSegment;
    if (rowName) {
      finalSegment += '-' + rowName;
    }
    if (columnName) {
      finalSegment += '-' + columnName;
    }

    segments.push(finalSegment); // Add back the modified last segment
  }

  // Reconstruct the URL
  const productUrlPath = segments.join('/');

  return `https://www.wowcher.co.uk${productUrlPath}`;
};

/**
 * Checks if the item is refurbished based on description or title
 *
 * @param {object} dealData - The DOD JSON data
 * @returns {boolean} True if the item is refurbished
 */
const isRefurbished = (dealData) => {
  const description = (dealData.description || '').toLowerCase();
  const title = (dealData.title || '').toLowerCase();

  return description.includes('refurbished') || title.includes('refurbished');
};

/**
 * Fetch deal data from API and create schema
 *
 * @param {number} dealId - The deal ID to fetch
 * @param {Array} allowedDealIds - Optional array of allowed deal IDs
 * @returns {Promise<object | null>} Schema object or null if deal not allowed
 */
export const createEnhancedSchema = (dealData, allowedDealIds = null) => {
  if (!dealData) {
    console.log('No mainDeal data found');

    return;
  }
  const result = createProductGroup(dealData, allowedDealIds);
  console.log('Create Product Group', result);

  return result;
};
